import { useState, useRef, type ChangeEvent } from 'react';
import { Upload, FileText, CircleAlert as AlertCircle, Check } from 'lucide-react';
import { useApi } from '../../../lib/useApi';
import { logAccountActivity } from '../../../lib/activityLog';
import { Sentry } from '../../../lib/sentry';
import { normaliseSeniority, inferDepartment, inferBuyingRole, inferOrgPlacementConfidence } from '../../../lib/orgChartUtils';
import type { Contact } from '../../../types';
import LoadingSpinner from '../../common/LoadingSpinner';

interface Props {
  accountId: string;
  companyName: string;
  onUploaded: (contacts: Contact[]) => void;
}

function findColumnIndex(headers: string[], ...keywords: string[]): number {
  return headers.findIndex(h => keywords.some(k => h.includes(k)));
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

export default function ContactCsvUpload({ accountId, companyName, onUploaded }: Props) {
  const { apiPost } = useApi();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<{ count: number; sample: string[] } | null>(null);
  const [success, setSuccess] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setSuccess(false);
    setUploading(true);

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        setError('CSV file must contain a header row and at least one data row.');
        setUploading(false);
        return;
      }

      const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().replace(/[^a-z0-9_]/g, '_'));

      const nameIdx = findColumnIndex(headers, 'name', 'full_name', 'contact_name');
      const firstNameIdx = findColumnIndex(headers, 'first_name', 'first');
      const lastNameIdx = findColumnIndex(headers, 'last_name', 'last');
      const titleIdx = findColumnIndex(headers, 'title', 'job_title', 'role', 'position');
      const emailIdx = findColumnIndex(headers, 'email', 'email_address');
      const phoneIdx = findColumnIndex(headers, 'phone', 'mobile', 'telephone');
      const linkedinIdx = findColumnIndex(headers, 'linkedin', 'linkedin_url', 'linkedin_profile');
      const deptIdx = findColumnIndex(headers, 'department', 'dept');
      const locationIdx = findColumnIndex(headers, 'location', 'city', 'country');
      const seniorityIdx = findColumnIndex(headers, 'seniority', 'level');
      const companyIdx = findColumnIndex(headers, 'company', 'organisation', 'organization', 'org');

      const contacts: Omit<Contact, 'id' | 'created_at' | 'updated_at'>[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);
        if (cols.every(c => !c)) continue;

        let firstName = firstNameIdx >= 0 ? cols[firstNameIdx] || '' : '';
        let lastName = lastNameIdx >= 0 ? cols[lastNameIdx] || '' : '';

        if (!firstName && !lastName && nameIdx >= 0) {
          const fullName = cols[nameIdx] || '';
          const parts = fullName.split(/\s+/);
          firstName = parts[0] || '';
          lastName = parts.slice(1).join(' ');
        }

        if (!firstName && !lastName) continue;

        const title = titleIdx >= 0 ? cols[titleIdx] || '' : '';
        const rawSeniority = seniorityIdx >= 0 ? cols[seniorityIdx] || '' : '';
        const { seniority, level } = normaliseSeniority(rawSeniority, title);
        const rawDept = deptIdx >= 0 ? cols[deptIdx] || '' : '';
        const department = rawDept || inferDepartment(title);
        const buyingRole = inferBuyingRole(title);

        const contact: Omit<Contact, 'id' | 'created_at' | 'updated_at'> = {
          account_id: accountId,
          first_name: firstName,
          last_name: lastName,
          role: title,
          email: emailIdx >= 0 ? cols[emailIdx] || '' : '',
          phone: phoneIdx >= 0 ? cols[phoneIdx] || '' : '',
          organisation: companyIdx >= 0 ? cols[companyIdx] || companyName : companyName,
          linkedin_url: linkedinIdx >= 0 ? cols[linkedinIdx] || '' : '',
          department,
          seniority,
          location: locationIdx >= 0 ? cols[locationIdx] || '' : '',
          buying_committee_role: buyingRole,
          persona_match: '',
          persona_match_score: 0,
          use_case_relevance_score: 0,
          org_level: level,
          reports_to_id: null,
          org_placement_confidence: 0,
          notes: '',
          source: 'csv',
          department_id: null,
          is_inferred: false,
        };

        contact.org_placement_confidence = inferOrgPlacementConfidence(contact as Contact);
        contacts.push(contact);
      }

      if (contacts.length === 0) {
        setError('No valid contacts found in CSV. Ensure at least a Name or First Name column exists.');
        setUploading(false);
        return;
      }

      setPreview({
        count: contacts.length,
        sample: contacts.slice(0, 3).map(c => `${c.first_name} ${c.last_name} - ${c.role}`),
      });

      const { inserted } = await apiPost<{ inserted: Contact[] }>(
        `/v1/account/${accountId}/contacts/bulk`,
        { contacts },
      );
      if (inserted) {
        setSuccess(true);
        onUploaded(inserted);
        logAccountActivity(accountId, 'contacts_uploaded', `Uploaded ${inserted.length} contacts via CSV`);
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { context: 'csv_upload' } });
      setError(err instanceof Error ? err.message : 'Failed to process CSV');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-app-line-strong rounded-lg p-8 text-center hover:border-forest-400 transition-colors">
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          onChange={handleFile}
          className="hidden"
        />
        {uploading ? (
          <div className="space-y-3">
            <LoadingSpinner size="lg" />
            <p className="text-sm text-app-muted">Processing contacts...</p>
          </div>
        ) : success ? (
          <div className="space-y-3">
            <div className="w-12 h-12 bg-app-positive/10 rounded-lg flex items-center justify-center mx-auto">
              <Check className="w-6 h-6 text-app-positive" />
            </div>
            <p className="text-sm font-medium text-app-positive">
              {preview?.count} contacts imported successfully
            </p>
            <button
              onClick={() => { setSuccess(false); setPreview(null); fileRef.current?.click(); }}
              className="text-xs text-forest-600 hover:text-forest-700 font-medium"
            >
              Upload more contacts
            </button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} className="w-full space-y-3">
            <div className="w-12 h-12 bg-app-canvas rounded-lg flex items-center justify-center mx-auto">
              <Upload className="w-6 h-6 text-app-muted" />
            </div>
            <div>
              <p className="text-sm font-medium text-app-ink">Upload Contact List (CSV)</p>
              <p className="text-xs text-app-muted mt-1">
                Drag and drop or click to upload a .csv file
              </p>
            </div>
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-app-critical/10 border border-app-critical rounded-lg">
          <AlertCircle className="w-4 h-4 text-app-critical mt-0.5 flex-shrink-0" />
          <p className="text-xs text-app-critical">{error}</p>
        </div>
      )}

      {preview && !error && (
        <div className="flex items-start gap-2 p-3 bg-forest-50 border border-forest-200 rounded-lg">
          <FileText className="w-4 h-4 text-forest-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-forest-700">{preview.count} contacts detected</p>
            <div className="mt-1 space-y-0.5">
              {preview.sample.map((s, i) => (
                <p key={i} className="text-[11px] text-forest-600">{s}</p>
              ))}
              {preview.count > 3 && (
                <p className="text-[11px] text-forest-500">+{preview.count - 3} more</p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="p-3 bg-app-canvas rounded-lg">
        <p className="text-[11px] font-medium text-app-muted uppercase tracking-wider mb-2">Expected CSV Columns</p>
        <div className="flex flex-wrap gap-1.5">
          {['Name / First Name', 'Title / Role', 'Email', 'Phone', 'LinkedIn URL', 'Department', 'Location', 'Company'].map(col => (
            <span key={col} className="px-2 py-0.5 bg-app-surface border border-app-line rounded text-[10px] text-app-muted">{col}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
